import React from "react";
import { Box, ClickAwayListener, Divider, Popper, Typography, useTheme } from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router";
import { StyledFilterPaper } from "../../common/components/filterPopover/FilterPopover";
import { useTranslation } from "@localization/useTranslation";
import { AiChatSourceReference } from "../AiChat.types";
import { useEntityUrlForDocumentEntityType } from "./useEntityUrl";

interface AiChatCitationPopoverProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    source: AiChatSourceReference;
    onClose: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onEntityLinkNavigate: () => void;
    onOpenPreview: () => void;
}

function renderCellWithBreaks(children: React.ReactNode): React.ReactNode {
    return React.Children.map(children, (child) => {
        if (typeof child !== "string" || !/<br\s*\/?>/i.test(child)) return child;
        const parts = child.split(/<br\s*\/?>/i);
        return parts.flatMap((part, i) => (i === 0 ? [part] : [<br key={`br-${i}`} />, part]));
    });
}

function fileIconFor(fileName: string | undefined) {
    const ext = (fileName ?? "").toLowerCase().split(".").pop() ?? "";
    if (ext === "pdf") return PictureAsPdfOutlinedIcon;
    if (["doc", "docx", "txt", "rtf", "odt"].includes(ext)) return DescriptionOutlinedIcon;
    if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(ext)) return ImageOutlinedIcon;
    return InsertDriveFileOutlinedIcon;
}

function AiChatCitationPopover({
    open,
    anchorEl,
    source,
    onClose,
    onMouseEnter,
    onMouseLeave,
    onEntityLinkNavigate,
    onOpenPreview,
}: AiChatCitationPopoverProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { translateDate, translateString } = useTranslation();

    const entityUrl = useEntityUrlForDocumentEntityType(source.entityType, source.entityId);
    const FileIcon = fileIconFor(source.fileName);
    const chunk = source.chunkContent ?? "";

    function handleEntityClick(event: React.MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (!entityUrl) return;
        onEntityLinkNavigate();
        navigate(entityUrl);
    }

    if (!open || !anchorEl) return null;

    return (
        <Popper
            open={open}
            anchorEl={anchorEl}
            placement="top"
            style={{ zIndex: theme.zIndex.tooltip }}
            modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
        >
            <ClickAwayListener onClickAway={onClose}>
                <StyledFilterPaper
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    sx={{
                        width: 440,
                        maxWidth: "90vw",
                        padding: theme.spacing(1.5, 2),
                        minWidth: 0,
                    }}
                >
                    {entityUrl ? (
                        <Box
                            component="a"
                            href={entityUrl}
                            onClick={handleEntityClick}
                            sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                textDecoration: "none",
                                display: "block",
                                "&:hover": { textDecoration: "underline" },
                            }}
                        >
                            {source.entityTitle}
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.primary }}>{source.entityTitle}</Typography>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", maxHeight: 260, overflow: "auto" }}>
                        <FormatQuoteIcon sx={{ fontSize: 26, color: theme.palette.grey[400], transform: "scaleX(-1)", flexShrink: 0 }} />
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontSize: 12.5,
                                lineHeight: 1.55,
                                color: theme.palette.text.primary,
                                "& p": { margin: theme.spacing(0.5, 0) },
                                "& p:first-of-type": { marginTop: 0 },
                                "& p:last-of-type": { marginBottom: 0 },
                                "& ul, & ol": { margin: theme.spacing(0.5, 0), paddingLeft: theme.spacing(2.5) },
                                "& li": { marginBottom: theme.spacing(0.25) },
                                "& h1, & h2, & h3, & h4, & h5, & h6": {
                                    fontSize: 13,
                                    fontWeight: 600,
                                    margin: theme.spacing(0, 0, 0.25, 0),
                                },
                                "& strong": { fontWeight: 600 },
                                "& em": { fontStyle: "italic" },
                                "& code": {
                                    backgroundColor: theme.palette.grey[100],
                                    borderRadius: theme.spacing(0.5),
                                    padding: theme.spacing(0.25, 0.5),
                                    fontSize: 11.5,
                                },
                                "& pre": {
                                    backgroundColor: theme.palette.grey[100],
                                    borderRadius: theme.spacing(0.75),
                                    padding: theme.spacing(0.75),
                                    overflowX: "auto",
                                    margin: theme.spacing(0.5, 0),
                                    "& code": { backgroundColor: "transparent", padding: 0 },
                                },
                                "& a": {
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    "&:hover": { textDecoration: "underline" },
                                },
                                "& blockquote": {
                                    borderLeft: `3px solid ${theme.palette.grey[300]}`,
                                    margin: theme.spacing(0.5, 0),
                                    paddingLeft: theme.spacing(1),
                                    color: theme.palette.text.secondary,
                                },
                                "& table": {
                                    borderCollapse: "collapse",
                                    fontSize: 11.5,
                                    margin: theme.spacing(0.5, 0),
                                },
                                "& th, & td": {
                                    border: `1px solid ${theme.palette.divider}`,
                                    padding: theme.spacing(0.25, 0.75),
                                    textAlign: "left",
                                },
                                "& th": { backgroundColor: theme.palette.grey[100], fontWeight: 600 },
                            }}
                        >
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    td: ({ children, ...rest }) => <td {...rest}>{renderCellWithBreaks(children)}</td>,
                                    th: ({ children, ...rest }) => <th {...rest}>{renderCellWithBreaks(children)}</th>,
                                }}
                            >
                                {chunk}
                            </ReactMarkdown>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Box
                        onClick={onOpenPreview}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            borderRadius: 1,
                            padding: theme.spacing(1),
                            "&:hover": {
                                backgroundColor: theme.palette.grey[100],
                            },
                        }}
                    >
                        <FileIcon sx={{ fontSize: 24, color: theme.palette.error.main }} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {source.documentTitle}
                            </Typography>
                            {(source.pageNumber != null || source.documentDate) && (
                                <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>
                                    {source.pageNumber != null && `${translateString("page")} ${source.pageNumber}`}
                                    {source.pageNumber != null && source.documentDate && " · "}
                                    {source.documentDate && translateDate(new Date(source.documentDate))}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </StyledFilterPaper>
            </ClickAwayListener>
        </Popper>
    );
}

export default AiChatCitationPopover;
